import { Box, Card, Skeleton, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { CopyToClipboardContent } from '@verantos-dev/shared-components';

import { useSqlSection } from './useSqlSection';

/**
 * Read-only SQL view promoted to a top-level Definition section (per
 * the gen-3 design — no more nested DSL-to-UI / SQL toggle from v0).
 *
 * SQL is generated on-the-fly via the platform's translate endpoint
 * (see `useCohortTranslationQuery`), formatted with `sql-formatter`,
 * and rendered through `react-syntax-highlighter` for parity with
 * the legacy SQL view.
 *
 * Width-fluid: the highlighter scrolls horizontally for long lines
 * and the outer card never forces overflow on narrow screens.
 */
export const SqlSection = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { sqlValue, isLoading, hasUnresolvedRefs } = useSqlSection();

  return (
    <Card
      data-testid="cohort-detail-sql"
      variant="outlined"
      sx={{
        // Figma section card: 24px padding, 24px title-to-content gap.
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
      }}
    >
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 600 }}
        data-testid="cohort-detail-sql-title"
      >
        Cohort definition SQL
      </Typography>
      {isLoading && !sqlValue ? (
        <Skeleton variant="rectangular" height={120} data-testid="cohort-detail-sql-skeleton" />
      ) : hasUnresolvedRefs ? (
        // Backend short-circuited because the cohort references a library AV
        // that hasn't been bound yet. Decoupled from save state (OR-6067) —
        // the cohort is saved, we just can't compile SQL for it yet.
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="cohort-detail-sql-unresolved-refs"
        >
          One or more criteria reference variables that haven't been resolved yet. SQL preview and
          cohort execution will be available once every criterion is bound.
        </Typography>
      ) : !sqlValue ? (
        <Typography variant="body2" color="text.secondary" data-testid="cohort-detail-sql-empty">
          SQL will appear here once the cohort definition is generated.
        </Typography>
      ) : (
        <CopyToClipboardContent
          id="copy-sql-button"
          copyText={sqlValue}
          copyButtonLabel={intl.formatMessage({
            id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copy',
          })}
          copiedButtonLabel={intl.formatMessage({
            id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copied',
          })}
          copiedTooltipTitle={intl.formatMessage({
            id: 'projects.cohortBuilderPage.cohortBuilderSqlContent.copiedToClipboard',
          })}
        >
          <Box
            data-testid="cohort-detail-sql-content"
            sx={{
              // Matches v0 SQL section: light-prism theme inside a
              // tinted background, scroll horizontal at narrow widths.
              backgroundColor: theme.palette.action.hover,
              borderRadius: 1,
              overflowX: 'auto',
              minWidth: 0,
              maxWidth: '100%',
              // Prism injects its own padding via the language stylesheet;
              // strip our outer padding so we don't double up.
              // Right padding leaves room for the absolutely positioned copy button.
              '& pre': {
                margin: 0,
                padding: '16px 140px 16px 16px !important',
                fontSize: `${theme.typography.caption.fontSize} !important`,
                lineHeight: '1.6 !important',
                backgroundColor: 'transparent !important',
              },
            }}
          >
            <SyntaxHighlighter language="sql" style={oneLight}>
              {sqlValue}
            </SyntaxHighlighter>
          </Box>
        </CopyToClipboardContent>
      )}
    </Card>
  );
};
